import { ActionReducer } from '@ngrx/store';
import { Action, ActionTypes } from './actions';
import { RunData } from '../../modules/run-management/definitions';

export type State = {
  query: {
    dateType?: string;
    fromDate?: string;
    toDate?: string;
    scheduleType: string[];
  };
  runNoteChanged: boolean;
  updatingRunNote: boolean;
  runNoteUpdateError: string | undefined;

  runStatusChanged: boolean;
  updatingRunStatus: boolean;
  runStatusUpdateError: string | undefined;

  fetchingRunData: boolean;
  runDataError: string | undefined;
  runData: RunData;
  count: number;

  fetchingRunExportData: boolean;
  runExportData: any[];
  runDataExportError: string | undefined;
};

export const defaultState: State = {
  query: { scheduleType: [] },
  runNoteChanged: false,
  updatingRunNote: false,
  runNoteUpdateError: undefined,
  runStatusChanged: false,
  updatingRunStatus: false,
  runStatusUpdateError: undefined,
  fetchingRunData: false,
  runDataError: undefined,
  runData: [],
  count: 0,
  runExportData: [],
  fetchingRunExportData: false,
  runDataExportError: undefined,
};

export const reducer: ActionReducer<State, Action> = (
  state: State = defaultState,
  action: Action,
): State => {
  switch (action.type) {
    case ActionTypes.FetchRunData:
    case ActionTypes.FetchRunDataSuccess:
    case ActionTypes.FetchRunDataFailure:
      return handleFetchRunDataAction(state, action);

    case ActionTypes.FetchRunExportData:
    case ActionTypes.FetchRunExportDataSuccess:
    case ActionTypes.FetchRunExportDataFailure:
      return handleFetchRunExportDataAction(state, action);

    case ActionTypes.UpdateRunStatus:
    case ActionTypes.ClearUpdateRunStatus:
    case ActionTypes.RunStatusUpdateSuccess:
    case ActionTypes.RunStatusUpdateFailure:
      return handleUpdateRunStatusAction(state, action);

    case ActionTypes.UpdateMultipleRunStatus:
      return handleUpdateMultipleRunStatusAction(state, action);

    case ActionTypes.UpdateRunNote:
    case ActionTypes.ClearUpdateRunNote:
    case ActionTypes.RunNoteUpdateSuccess:
    case ActionTypes.RunNoteUpdateFailure:
      return handleUpdateRunNoteAction(state, action);

    default:
      return state;
  }
};

export function handleFetchRunDataAction(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.FetchRunData:
      return {
        ...state,
        fetchingRunData: true,
        runDataError: undefined,
        query: {
          ...state.query,
          ...action.payload.searchParams,
        },
      };

    case ActionTypes.FetchRunDataSuccess:
      return {
        ...state,
        fetchingRunData: false,
        runData: [...action.payload.data],
        count: action.payload.count,
      };

    case ActionTypes.FetchRunDataFailure:
      return {
        ...state,
        fetchingRunData: false,
        runDataError: action.error.message,
      };

    default:
      return state;
  }
}

export function handleFetchRunExportDataAction(
  state: State,
  action: Action,
): State {
  switch (action.type) {
    case ActionTypes.FetchRunExportData:
      return {
        ...state,
        fetchingRunExportData: true,
        runDataExportError: undefined,
      };

    case ActionTypes.FetchRunExportDataSuccess:
      return {
        ...state,
        fetchingRunExportData: false,
        runExportData: action.payload,
      };

    case ActionTypes.FetchRunExportDataFailure:
      return {
        ...state,
        fetchingRunExportData: false,
        runDataExportError: action.error.message,
      };

    default:
      return state;
  }
}

export function handleUpdateRunStatusAction(
  state: State,
  action: Action,
): State {
  switch (action.type) {
    case ActionTypes.UpdateRunStatus:
      return {
        ...state,
        updatingRunStatus: true,
        runStatusUpdateError: undefined,
      };

    case ActionTypes.RunStatusUpdateSuccess:
      const { id, status } = action.payload;
      const runData = updateRunAndReturnRunData(state, id, status);
      return {
        ...state,
        runData,
        runStatusChanged: true,
        updatingRunStatus: false,
        runStatusUpdateError: undefined,
      };

    case ActionTypes.RunStatusUpdateFailure:
      return {
        ...state,
        runStatusChanged: false,
        updatingRunStatus: false,
        runStatusUpdateError: action.error.message,
      };

    case ActionTypes.ClearUpdateRunStatus:
      return {
        ...state,
        runStatusChanged: false,
        updatingRunStatus: false,
        runStatusUpdateError: undefined,
      };

    default:
      return state;
  }
}

export function handleUpdateMultipleRunStatusAction(
  state: State,
  action: Action,
): State {
  switch (action.type) {
    case ActionTypes.UpdateMultipleRunStatus:
      return {
        ...state,
        updatingRunStatus: true,
        runStatusUpdateError: undefined,
      };

    default:
      return state;
  }
}

export function handleUpdateRunNoteAction(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.UpdateRunNote:
      return {
        ...state,
        updatingRunNote: true,
        runNoteUpdateError: undefined,
      };

    case ActionTypes.RunNoteUpdateSuccess:
      const { id, notes } = action.payload;
      const runData = updateScheduleNoteAndReturnRunData(state, id, notes);
      return {
        ...state,
        runData,
        runNoteChanged: true,
        updatingRunNote: false,
        runNoteUpdateError: undefined,
      };

    case ActionTypes.RunNoteUpdateFailure:
      return {
        ...state,
        runNoteChanged: false,
        updatingRunNote: false,
        runNoteUpdateError: action.error.message,
      };

    case ActionTypes.ClearUpdateRunNote:
      return {
        ...state,
        runNoteChanged: false,
        updatingRunNote: false,
        runNoteUpdateError: undefined,
      };

    default:
      return state;
  }
}

export const updateScheduleNoteAndReturnRunData = (
  state: State,
  scheduleId: string,
  note: string,
) => {
  const { runData } = state;
  const schedule = state.runData.find(({ runId }) => runId === scheduleId);
  schedule.notes = note;

  return runData;
};

export const updateRunAndReturnRunData = (
  state: State,
  id: number,
  status: string,
) => {
  const { runData } = state;

  const schedule = runData.find(({ items }) =>
    items.some(({ runItemId }) => runItemId === id),
  );

  schedule.items.forEach(item => {
    if (item.runItemId === id) item.scheduleStatus = status;
    return item;
  });

  return runData;
};

export default reducer;
